import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import KitForm from "../components/KitForm";
import SocialProof from "../components/SocialProof";
import RotatingWord from "../components/RotatingWord";
import Marquee from "../components/Marquee";
import StatsStrip from "../components/StatsStrip";
import WhisperWall from "../components/WhisperWall";
import useParallax from "../hooks/useParallax";

// Parallax tuning — speed = px translated per px scrolled; max = clamp
const ORB_SLOW_SPEED = 0.18;
const ORB_SLOW_MAX_PX = 220;
const ORB_FAST_SPEED = 0.35;
const ORB_FAST_MAX_PX = 280;
const ORB_MID_SPEED = -0.22;
const ORB_MID_MAX_PX = 220;

const ARTICLES = [
  {
    path: "/notion",
    number: "01",
    label: "Workspace",
    title: "The Notion Workspace Architecture",
    teaser:
      "How to design a calm, minimalist Notion dashboard that protects your focus and tracks every project without the clutter.",
    minutes: "9 min",
    testId: "article-card-notion",
  },
  {
    path: "/automation",
    number: "02",
    label: "Systems",
    title: "The Visual Automation Blueprint",
    teaser:
      "The mental model behind Make.com workflows that quietly buy back 20+ hours a week — without writing a single line of code.",
    minutes: "11 min",
    testId: "article-card-automation",
  },
  {
    path: "/stack",
    number: "03",
    label: "AI Tools",
    title: "The Solopreneur AI Stack",
    teaser:
      "A curated breakdown of design-forward AI tools letting one-person studios out-ship entire teams.",
    minutes: "10 min",
    testId: "article-card-stack",
  },
];

export default function Home() {
  const orbSlow = useParallax(ORB_SLOW_SPEED, ORB_SLOW_MAX_PX);
  const orbFast = useParallax(ORB_FAST_SPEED, ORB_FAST_MAX_PX);
  const orbMid  = useParallax(ORB_MID_SPEED, ORB_MID_MAX_PX);

  return (
    <main data-testid="home-page" className="relative overflow-x-clip">
      {/* Ambient aurora — sits at top, always visible behind everything */}
      <div aria-hidden="true" className="aurora" />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative px-6 md:px-12 lg:px-24 pt-20 md:pt-32 pb-20 md:pb-32 max-w-7xl mx-auto"
      >
        {/* Dot grid background */}
        <div
          aria-hidden="true"
          className="dot-grid absolute inset-0 -z-10"
        />
        {/* Sage glow blob — parallax slow */}
        <div
          aria-hidden="true"
          className="glow-orb orb-drift hidden md:block"
          style={{
            width: "640px",
            height: "640px",
            top: "-160px",
            right: "-200px",
            transform: `translateY(${orbSlow}px)`,
          }}
        />
        <div
          aria-hidden="true"
          className="glow-orb glow-orb-amber orb-drift"
          style={{
            width: "460px",
            height: "460px",
            bottom: "-140px",
            left: "-100px",
            animationDelay: "-7s",
            transform: `translateY(${orbMid}px)`,
          }}
        />
        <div
          aria-hidden="true"
          className="glow-orb glow-orb-rose orb-drift hidden md:block"
          style={{
            width: "380px",
            height: "380px",
            top: "40%",
            left: "30%",
            animationDelay: "-3s",
            transform: `translateY(${orbFast}px)`,
          }}
        />

        <div className="relative max-w-5xl">
          {/* Pinned-pin overline with sticker */}
          <div className="fade-up flex flex-wrap items-center gap-3">
            <span
              className="sticker sticker-tilt-left"
              data-testid="hero-overline-sticker"
            >
              <span
                className="w-1.5 h-1.5 rounded-full pulse-dot"
                style={{ background: "var(--accent)" }}
              />
              Issue No. 47 · Live this Friday
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-tertiary">
              Pinned from Pinterest? Welcome in.
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="fade-up-1 mt-8 font-display text-primary text-5xl md:text-7xl lg:text-[92px] font-light tracking-tight leading-[1.0]"
          >
            Quiet systems for{" "}
            <RotatingWord />
            <br />
            <span className="sweep-underline">solo studios.</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="fade-up-2 mt-8 text-lg md:text-xl text-secondary leading-relaxed max-w-2xl"
          >
            A private letter on workspace architecture, visual automation, and
            the AI stack quietly running modern one-person businesses. Read by
            founders who build calm companies on purpose.
          </p>

          <div className="fade-up-3 mt-12">
            <KitForm testId="hero-kit-form" />
          </div>

          <div className="fade-up-4">
            <SocialProof />
          </div>

          <div className="fade-up-5 mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-tertiary">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.5} />
              One letter / week
            </span>
            <span>·</span>
            <span>No filler</span>
            <span>·</span>
            <span>Unsubscribe anytime</span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* STATS */}
      <StatsStrip />

      {/* DIVIDER */}
      <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
        <div className="rule-with-label">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary">
            03 Field Notes · Free to read
          </span>
        </div>
      </div>

      {/* ARTICLE CARDS — bigger, with oversized index numbers */}
      <section
        data-testid="articles-section"
        className="px-6 md:px-12 lg:px-24 py-16 md:py-24 max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <h2 className="font-display text-primary text-3xl md:text-5xl font-light tracking-tight leading-[1.08] max-w-2xl">
            Three quiet blueprints. Built by hand.
            <br />
            <span className="italic font-extralight text-secondary">Sharpened in public.</span>
          </h2>
          <p className="text-secondary text-base md:text-[15px] max-w-sm leading-relaxed">
            Start here. Each guide is a self-contained system you can ship into
            your own studio this week.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {ARTICLES.map((a) => (
            <Link
              key={a.path}
              to={a.path}
              data-testid={a.testId}
              className="group card-tilt relative flex flex-col p-7 md:p-9 rounded-3xl bg-card-surface border border-subtle hover:border-strong hover:bg-surface min-h-[360px] md:min-h-[440px] overflow-hidden"
            >
              {/* Oversized index */}
              <div className="absolute -top-2 -right-2 select-none pointer-events-none">
                <span className="card-index">{a.number}</span>
              </div>

              <div className="flex items-center justify-between relative z-10">
                <span className="sticker">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "var(--accent)" }}
                  />
                  {a.label}
                </span>
                <ArrowUpRight
                  className="w-5 h-5 text-tertiary group-hover:text-[var(--accent)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300"
                  strokeWidth={1.5}
                />
              </div>

              <h3 className="mt-auto font-display text-primary text-xl md:text-2xl font-medium tracking-tight leading-snug pt-16 relative z-10">
                {a.title}
              </h3>

              <p className="mt-4 text-secondary text-[14px] md:text-[15px] leading-relaxed relative z-10">
                {a.teaser}
              </p>

              <div className="mt-6 pt-5 border-t border-subtle flex items-center justify-between relative z-10">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary">
                  {a.minutes} read
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary group-hover:text-primary transition-colors">
                  Open field note →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHISPER WALL */}
      <WhisperWall />

      {/* FINAL CTA — second chance after social proof */}
      <section
        data-testid="home-final-cta"
        className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto pb-8"
      >
        <div className="relative bg-card-surface border border-subtle rounded-[2rem] p-8 md:p-16 overflow-hidden">
          <div
            aria-hidden="true"
            className="glow-orb orb-drift"
            style={{
              width: "520px",
              height: "520px",
              top: "-120px",
              right: "-120px",
              opacity: 0.7,
              transform: `translateY(${orbSlow * 0.4}px)`,
            }}
          />
          <div className="relative z-10 flex flex-col items-start">
            <span className="sticker sticker-tilt-right">
              <span
                className="w-1.5 h-1.5 rounded-full pulse-dot"
                style={{ background: "var(--accent)" }}
              />
              Last call · Friday delivery
            </span>
            <h2 className="mt-6 font-display text-primary text-3xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-2xl">
              The Solo Studio Blueprint
            </h2>
            <p className="mt-5 max-w-xl text-secondary text-base md:text-lg leading-relaxed">
              Enter your email to get the 2026 Solo Studio Blueprint delivered instantly, plus my weekly dispatch on visual automation and the AI stack.
            </p>
            <div className="mt-10 w-full md:max-w-lg">
              <KitForm testId="home-final-kit-form" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
